// Implementation is based on reach-ui Popover component implementation

import * as React from "react";
import ReactDOM from "react-dom";

interface IPopoverProps {
    children?: React.ReactNode;
    targetRef?: React.RefObject<HTMLElement>;
    className?: string;
}

type Position = (
    targetRect?: DOMRect | null,
    popoverRect?: DOMRect | null,
) => React.CSSProperties;

interface Collisions {
    top: boolean;
    right: boolean;
    bottom: boolean;
    left: boolean;
}

function getCollisions(targetRect: DOMRect, popoverRect: DOMRect) {
    const collisions = {
        top: targetRect.top - popoverRect.height < 0,
        right: window.innerWidth < targetRect.left + popoverRect.width,
        bottom: window.innerHeight < targetRect.bottom + popoverRect.height,
        left: targetRect.left + targetRect.width - popoverRect.width < 0,
    };

    const directionRight = collisions.right && !collisions.left;
    const directionLeft = collisions.left && !collisions.right;
    const directionUp = collisions.bottom && !collisions.top;
    const directionDown = collisions.top && !collisions.bottom;

    return {
        collisions,
        directionRight,
        directionLeft,
        directionUp,
        directionDown,
    };
}

function useRect(
    initialRef?: React.RefObject<HTMLElement>,
): [DOMRect, React.RefObject<HTMLElement> | React.RefCallback<HTMLElement>] {
    const [rect, setRect] = React.useState<DOMRect>({
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        height: 0,
        width: 0,
        x: 0,
        y: 0,
        toJSON: () => {},
    });
    const callbackRef = React.useCallback((node) => {
        if (node !== null) {
            setRect(node.getBoundingClientRect());
        }
    }, []);
    React.useEffect(() => {
        const node = initialRef?.current;

        if (node && node !== null) {
            setRect(node.getBoundingClientRect());
        }
    }, [initialRef]);
    const ref = initialRef || callbackRef;
    return [rect, ref];
}

function getTopPosition(
    targetRect: DOMRect,
    popoverRect: DOMRect,
    isDirectionUp: boolean,
    collisions: Collisions,
) {
    let correctionTopOffset = 0;

    // This case means there is not enough space for properly positioned popover
    // in both up or down directions
    if (collisions.top && collisions.bottom) {
        // If there is enough height for popover
        // let's correct top position
        if (window.innerHeight >= popoverRect.height) {
            const bottomOverflow =
                window.innerHeight - (targetRect.bottom + popoverRect.height);
            correctionTopOffset = bottomOverflow;
        }
    }

    return {
        top: isDirectionUp
            ? `${targetRect.top - popoverRect.height + window.pageYOffset}px`
            : `${targetRect.top +
                  targetRect.height +
                  window.pageYOffset +
                  correctionTopOffset}px`,
    };
}

const positionDefault = (targetRect: DOMRect, popoverRect: DOMRect) => {
    if (!targetRect || !popoverRect) {
        return {};
    }

    const { collisions, directionRight, directionUp } = getCollisions(
        targetRect,
        popoverRect,
    );

    return {
        left: directionRight
            ? `${targetRect.right - popoverRect.width + window.pageXOffset}px`
            : `${targetRect.left + window.pageXOffset}px`,
        ...getTopPosition(targetRect, popoverRect, directionUp, collisions),
    };
};

function getStyles(
    position: Position,
    targetRect: DOMRect | null,
    popoverRect: DOMRect | null,
): React.CSSProperties {
    return popoverRect
        ? position(targetRect, popoverRect)
        : { visibility: "hidden" };
}

export function Popover({ children, targetRef, className }: IPopoverProps) {
    const [targetRect] = useRect(targetRef);
    const [popoverRect, popoverRef] = useRect();
    const position = positionDefault;

    return ReactDOM.createPortal(
        <div
            // @ts-ignore
            ref={popoverRef}
            tabIndex={-1}
            style={{
                position: "absolute",
                // @ts-ignore
                ...getStyles(position, targetRect, popoverRect),
            }}
            className={className}
        >
            {children}
        </div>,
        document.querySelector("body")!,
    );
}
